import CloseIcon from '@mui/icons-material/Close';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { REQUIRED_RED } from '../../../App';
import { formatCurrency } from '../../../utility/helpers/displayHelper';
import { handlePreventKeyDownDecimal } from '../../../utility/helpers/commonFunction';
import { amountSupplierInvoiceApi } from '../../../redux/Supplier/action';

/**
 * Styles for common UI elements
 */
const styles = {
    // Dialog header styles
    dialogHeader: {
        height: '64px',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },

    // Dialog content styles
    dialogContent: {
        padding: '16px!important',
        maxHeight: '750px',
        overflow: 'auto',
    },

    // Table container
    tableContainer: {
        backgroundColor: '#F1F1F1',
        borderRadius: '4px',
        marginBottom: '16px',
        marginTop: '16px',
        padding: '12px',
        maxHeight: '140px',
        overflow: 'auto',
    },

    // Table header cell styles
    tableHeaderCell: {
        padding: '4px 0',
        fontWeight: 500,
        fontSize: '16px',
        color: '#000',
        backgroundColor: '#F1F1F1',
        lineHeight: '24px',
        letterSpacing: '0.15px',
        border: 'none',
    },

    // Table body cell styles
    tableBodyCell: {
        padding: '4px 0',
        fontSize: '16px',
        lineHeight: '150%',
        backgroundColor: '#F1F1F1',
        border: 'none',
    },

    // Form fields container
    formFields: {
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        marginTop: '16px',
    },

    // Input field
    inputField: {
        width: '100%',
    },

    // Actions container
    actionsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px',
    },

    // Cancel button
    cancelButton: {
        color: '#1976d2',
        borderColor: '#1976d2',
        '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
        },
    },

    // Record payment button
    recordButton: {
        backgroundColor: '#1976d2',
        color: 'white',
        '&:hover': {
            backgroundColor: '#1565c0',
        },
    },

    // Label text styles
    labelText: {
        fontSize: 16,
        fontWeight: 500,
        marginBottom: '8px',
        letterSpacing: 0.15,
        lineHeight: '150%',
    },

    // Amount container
    amountContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
    },
};

/**
 * RecordPaymentModal - Displays payment information and allows recording of payments
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls whether the dialog is open
 * @param {Function} props.onClose - Handler function for closing the dialog
 * @param {Array} props.paymentRecords - Array of payment records to display
 * @param {number} props.totalDue - Total amount due
 * @param {number} props.remainingBalance - Remaining balance to be paid
 * @param {Function} props.onSave - Handler function for saving payment
 * @param {boolean} props.isLoading - Loading state for save operation
 * @param {Function} props.setIsLoading - Function to set loading state
 * @returns {React.Component}
 */
export default function RecordPaymentModal({
    open,
    onClose,
    paymentRecords = [],
    totalDue = 0,
    remainingBalance = 0,
    onSave,
    isLoading = false,
    setIsLoading,
    selectedId,
}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [invoiceAmountDetails, setInvoiceAmountDetails] = useState(null);

    // Initialize react-hook-form
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: {
            paymentAmount: '',
            paymentReference: '',
        },
    });
    const paymentAmounteValue = useWatch({ control, name: 'paymentAmount' });
    const pendingAmount = invoiceAmountDetails?.pendingAmount || 0;

    // Convert string to number safely
    const enteredAmount = parseFloat(paymentAmounteValue) || 0;
    const remainingAmount = pendingAmount - enteredAmount;
    console.log('remainingAmount', remainingAmount);
    //Handle close events
    const handleClose = () => {
        onClose();
        reset();
    };

    // Handle form submission
    const handleFormSubmit = (data) => {
        if (onSave) {
            // Only pass the amount to onSave as per your usage example
            onSave(data.paymentAmount, data.paymentReference);
        }
        // Don't close the modal here as it will be closed in the parent component after API call
        reset();
    };

    // Format currency
    useEffect(() => {
        const fetchInvoiceAmount = async () => {
            if (open && selectedId) {
                try {
                    const response = await amountSupplierInvoiceApi(selectedId);
                    setInvoiceAmountDetails(response);
                } catch (error) {
                    console.error('Failed to fetch invoice amount details:', error);
                }
            }
        };

        fetchInvoiceAmount();
    }, [open, selectedId]);

    console.log('invoiceAmountDetails', invoiceAmountDetails);

    const paymentsAmountGet = invoiceAmountDetails?.payments;
    return (
        <Dialog open={open} onClose={handleClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
            {/* Dialog Header */}
            <DialogTitle sx={styles.dialogHeader}>
                <Typography variant="h6">Do you want to record payment?</Typography>
                <IconButton onClick={handleClose} disabled={isLoading}>
                    <CloseIcon style={{ fill: 'black', fontSize: 32 }} />
                </IconButton>
            </DialogTitle>

            {/* Dialog Content */}
            <DialogContent sx={styles.dialogContent}>
                {/* Total due */}
                <div style={styles.amountContainer}>
                    <Typography variant="body1">Total due:</Typography>
                    <Typography variant="body1" fontWeight={500}>
                        {formatCurrency(invoiceAmountDetails?.pendingAmount)}
                    </Typography>
                </div>
                {paymentsAmountGet?.length > 0 && (
                    <>
                        {/* Payment history table */}
                        <TableContainer component={Paper} sx={styles.tableContainer} elevation={0}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow style={{ border: 'none' }}>
                                        <TableCell sx={styles.tableHeaderCell}>Amount</TableCell>
                                        <TableCell sx={styles.tableHeaderCell}>Date</TableCell>
                                        <TableCell sx={styles.tableHeaderCell}>Reference</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paymentsAmountGet && paymentsAmountGet.length > 0 ? (
                                        paymentsAmountGet.map((payment, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={styles.tableBodyCell}>{payment.amountPaid ? formatCurrency(payment.amountPaid) : ''}</TableCell>

                                                <TableCell sx={styles.tableBodyCell}>
                                                    {payment.paymentDate
                                                        ? new Date(payment.paymentDate).toLocaleDateString('en-US', {
                                                              year: 'numeric',
                                                              month: 'long',
                                                              day: '2-digit',
                                                          })
                                                        : ''}
                                                </TableCell>
                                                <TableCell sx={styles.tableBodyCell}>{payment?.notes ? payment?.notes : '-'}</TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={styles.tableBodyCell}>
                                                No payment records found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Remaining balance */}
                        <div style={styles.amountContainer}>
                            <Typography variant="body1">Remaining balance:</Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {formatCurrency(remainingAmount)}
                            </Typography>
                        </div>
                    </>
                )}

                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    {/* Payment form fields */}
                    <div style={styles.formFields}>
                        <div style={{ flex: 1 }}>
                            <Typography sx={styles.labelText}>
                                Payment amount <span style={{ color: REQUIRED_RED }}> *</span>
                            </Typography>
                            <Controller
                                name="paymentAmount"
                                control={control}
                                rules={{
                                    required: 'Cost Per Unit is required',
                                    validate: (value) => {
                                        if (value === '.') {
                                            return 'Cost cannot be just a decimal point.';
                                        }
                                        if (Number(value) === 0) {
                                            return 'Cost cannot be 0';
                                        }

                                        return true;
                                    },
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        sx={styles.inputField}
                                        variant="outlined"
                                        disabled={isLoading}
                                        error={!!errors.paymentAmount}
                                        helperText={errors.paymentAmount?.message}
                                        InputProps={{
                                            startAdornment: <span style={{ marginRight: 8 }}>$</span>,
                                        }}
                                        onKeyDown={handlePreventKeyDownDecimal}
                                        onChange={(event) => {
                                            const inputValue = event.target.value;
                                            const numericValue = inputValue.replace(/[^\d.]/g, '');
                                            field.onChange(numericValue);
                                        }}
                                    />
                                )}
                            />
                        </div>

                        <div style={{ flex: 1 }}>
                            <Typography sx={styles.labelText}>Payment reference</Typography>
                            <Controller
                                name="paymentReference"
                                control={control}
                                render={({ field }) => <TextField {...field} sx={styles.inputField} variant="outlined" placeholder="" disabled={isLoading} />}
                            />
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div style={styles.actionsContainer}>
                        <Button variant="outlined" onClick={handleClose} sx={styles.cancelButton} disabled={isLoading}>
                            CANCEL
                        </Button>
                        <Button
                            variant="contained"
                            type="submit"
                            sx={styles.recordButton}
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {isLoading ? 'PROCESSING...' : 'RECORD PAYMENT'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
